import React from "react";
import { FieldErrors, FormProvider, UseFormReturn } from "react-hook-form";
import { Button, Dialog, Form } from "@sscs/ui";
import { AnalysisCapacityFields } from "Components";
import { PURL_SAFE_HELP_MESSAGE } from "Constants";
import { GroupFormData, GroupFormPercentageSlider } from "../../types";

type GroupDialogFormProps = {
  showSSOGroupMapping: boolean;
  onClose: () => void;
  onSubmit: React.FormEventHandler<HTMLFormElement>;
  errors: FieldErrors<GroupFormData>;
  formMethods: UseFormReturn<GroupFormData>;
  title: string;
  submitButton: React.ReactElement;
  filestreamPercentageFieldName: keyof GroupFormPercentageSlider;
  projectsPercentageFieldName: keyof GroupFormPercentageSlider;
  filestreamPercentage: number | null;
  projectsPercentage: number | null;
  onPercentageSliderChange: (value: number) => void;
  onProjectsPercentageInputChange: (value: string) => void;
  totalPortalCapacity?: number;
  filestreamUsageBytes?: number;
  projectsUsageBytes?: number;
};

const GroupDialogForm = ({
  onClose,
  showSSOGroupMapping,
  totalPortalCapacity,
  onSubmit,
  errors,
  formMethods,
  title,
  filestreamPercentageFieldName,
  projectsPercentageFieldName,
  filestreamPercentage,
  projectsPercentage,
  onPercentageSliderChange,
  onProjectsPercentageInputChange,
  submitButton,
  filestreamUsageBytes,
  projectsUsageBytes,
}: GroupDialogFormProps): React.JSX.Element => {
  return (
    <>
      <Dialog.Header>
        <Dialog.Header.Title>{title}</Dialog.Header.Title>
      </Dialog.Header>
      <FormProvider {...formMethods}>
        <Form.Form onSubmit={onSubmit} name="group-form">
          <Dialog.Body>
            <Dialog.Section>
              <Dialog.Section.Title>Group details</Dialog.Section.Title>
              <Dialog.Section.Content>
                <Form.TextField
                  label="Group name"
                  name="name"
                  id="name"
                  isFullWidth
                  isRequired
                  errors={errors}
                  helperText={PURL_SAFE_HELP_MESSAGE}
                />
                {showSSOGroupMapping && (
                  <Form.Autocomplete
                    label="Map to SSO groups"
                    name="oidc_sso_group_mappings"
                    id="oidc_sso_group_mappings"
                    freeSolo
                    options={[]}
                    multiSelect
                    placeholder="Type and press Enter"
                    helperText="List of groups to map from your identity provider"
                    errors={errors}
                  />
                )}
                <Form.Checkbox
                  label="Default group"
                  name="is_default"
                  id="is_default"
                  errors={errors}
                />
                <Form.TextArea
                  label="Description"
                  name="description"
                  id="description"
                  isFullWidth
                  errors={errors}
                />
              </Dialog.Section.Content>
            </Dialog.Section>
            <Dialog.Section>
              <Dialog.Section.Title>Monthly limit</Dialog.Section.Title>
              <Dialog.Section.Content>
                <AnalysisCapacityFields
                  groupsCount={1}
                  totalPortalCapacity={totalPortalCapacity}
                  filestreamUsageBytes={filestreamUsageBytes ?? 0}
                  projectsUsageBytes={projectsUsageBytes ?? 0}
                  filestreamPercentageFieldName={filestreamPercentageFieldName}
                  projectsPercentageFieldName={projectsPercentageFieldName}
                  filestreamPercentage={filestreamPercentage}
                  projectsPercentage={projectsPercentage}
                  isRequired={false}
                  onPercentageSliderChange={onPercentageSliderChange}
                  onProjectsPercentageInputChange={
                    onProjectsPercentageInputChange
                  }
                />
              </Dialog.Section.Content>
            </Dialog.Section>
          </Dialog.Body>
          <Dialog.Footer>
            <Dialog.Footer.Actions>
              <Button color="secondary" onClick={onClose}>
                Cancel
              </Button>
              {submitButton}
            </Dialog.Footer.Actions>
          </Dialog.Footer>
        </Form.Form>
      </FormProvider>
    </>
  );
};

export default GroupDialogForm;
